import { useAuth } from '@clerk/react-router'
import { useHotkey } from '@tanstack/react-hotkeys'
import { ChevronsUpDownIcon, MenuIcon, SearchIcon, XIcon } from 'lucide-react'
import { useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import type { components } from '@joyful/openapi/types.gen'
import {
  pluralize,
  UserRoleManagementCapabilities,
  UserRoleSchema,
} from '@joyful/utils'

import type { ButtonProps, ItemProps, PopoverProps } from '../../../core'
import {
  Box,
  Button,
  Copyable,
  Enumerate,
  IconButton,
  Item,
  ItemContent,
  ItemGroup,
  ItemGroupLabel,
  ItemSeparator,
  ItemSubtitle,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Redact,
  Separator,
  Sheet,
  SheetCloseIconButton,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Stack,
  Tooltip,
  UserAvatar,
} from '../../../core'
import {
  AccountSelectionCommand,
  AccountSelectionCommandGroupedOptions,
  AccountSelectionCommandHeader,
  AccountSelectionCommandInput,
  AccountSelectionCommandList,
} from './AccountSelectionCommand'
import { AgentActivityNavbarButton } from '../ask-ai/AgentActivityNavbarButton'
import { AskAiNavbarButton } from '../ask-ai/AskAiNavbarButton'
import type { AppNavigationItem } from '../global-command/GlobalCommandCore'
import {
  getAppNavigationLinkTo,
  useGlobalCommand,
} from '../global-command/GlobalCommandCore'
import { MobileRoleImpersonationMenu } from '../../features/admin/components/SwitchRoleDropdownMenuItem'
import {
  getNavbarAppHref,
  Navbar,
  NavbarAppSwitcher,
  NavbarBrand,
  NavbarSection,
  OverflowNavbarLinkList,
} from './Navbar'
import type {
  NavbarApp,
  NavbarEnvironment,
  OverflowNavbarLinkListItem,
  OverflowNavbarLinkListProps,
} from './Navbar'
import { NavbarUserMenu } from './NavbarUserMenu'
import { DemoModeIndicator } from '../demo-mode-indicator'
import { DeploymentInfo } from '../DeploymentInfo'
import { RestrictToCapability } from '../RestrictToCapability'
import { LeaveFeedbackItem } from '../../features/feedback/components/LeaveFeedbackMenuItems'
import { useLeaveFeedbackContext } from '../../features/feedback/components/LeaveFeedbackProvider'
import { ProposeChangeItem } from '../../features/feedback/components/ProposeChangeMenuItems'
import { useProposeChangeContext } from '../../features/feedback/components/ProposeChangeContext'
import { useSetDemoMode } from '../../features/admin/hooks/useSetDemoMode'
import { GlobalHotkeys } from '../../utils/hotkeys'
import { setActiveOrgSlugCookie } from '../../utils/activeOrgSlugCookie'
import { useActiveAccount } from '../../utils/activeAccountContext'
import { useCurrentUser } from '../../utils/currentUserContext'
import { useHasFeatureFlag } from '../../hooks/useHasFeatureFlag'
import { useHasUserCapability } from '../../hooks/useHasUserCapability'
import { getAppSwitchRoutePath } from '../../features/navigation/utils/appSwitchRoutePath'
import { AnnouncementsMenuHintPopover } from '../../features/announcements/components/AnnouncementsMenuHintPopover'
import {
  NavbarAnnouncements,
  NavbarAnnouncementsHelpMenuItem,
} from '../../features/announcements/components/NavbarAnnouncements'
import { useApiQuery } from '../../utils/reactQuery'

const EMPTY_NAVIGATION_ITEMS: ReadonlyArray<AppNavigationItem> = []
type CurrentUserAccountRead = components['schemas']['CurrentUserAccountRead']

export interface JoyfulWebNavbarProps {
  accountItems?: ReadonlyArray<AppNavigationItem>
  activeApp: NavbarApp
  appSwitcherWrapper?: (switcher: ReactElement) => ReactNode
  currentUserSubmenuItems?: ReactNode
  environment: NavbarEnvironment
  frontendCommitMessage?: string | null
  frontendCommitSha?: string | null
  frontendCommitTimestamp?: string | null
  generalItems?: ReadonlyArray<AppNavigationItem>
  helpMenuItem?: ReactNode
  homePath: string
  isAccountSwitchPending?: boolean
  manageTeamHref?: string | null
  onSelectAccount: (account: CurrentUserAccountRead) => void
  primaryItems: ReadonlyArray<AppNavigationItem>
  renderPrimaryItem?: OverflowNavbarLinkListProps['renderVisibleItem']
  renderUserMenuTrigger?: (trigger: ReactElement) => ReactNode
  rightStartAdornment?: ReactNode
  utilityMenuItems?: ReactNode
}

export function JoyfulWebNavbar({
  accountItems = EMPTY_NAVIGATION_ITEMS,
  activeApp,
  appSwitcherWrapper,
  currentUserSubmenuItems,
  environment,
  frontendCommitMessage = null,
  frontendCommitSha = null,
  frontendCommitTimestamp = null,
  generalItems = EMPTY_NAVIGATION_ITEMS,
  helpMenuItem,
  homePath,
  isAccountSwitchPending = false,
  manageTeamHref = null,
  onSelectAccount,
  primaryItems,
  renderPrimaryItem,
  renderUserMenuTrigger,
  rightStartAdornment,
  utilityMenuItems,
}: JoyfulWebNavbarProps) {
  const activeAccount = useActiveAccount()
  const hasCapability = useHasUserCapability()
  const hasFeatureFlag = useHasFeatureFlag()
  const setDemoMode = useSetDemoMode()
  const { hash, pathname, search } = useLocation()
  const accountHref = getNavbarAppHref({ app: 'app', path: '/account' })
  const navbarLinkItems = createOverflowNavbarLinkListItems({
    items: primaryItems,
    pathname,
  })

  const appSwitcher = (
    <NavbarAppSwitcher
      activeApp={activeApp}
      getPathForApp={(app) => {
        return getAppSwitchRoutePath({
          app,
          hasCapability,
          hasFeatureFlag,
          hash,
          orgSlug: activeAccount?.slug ?? null,
          pathname,
          search,
        })
      }}
    />
  )
  const renderedAppSwitcher = appSwitcherWrapper
    ? appSwitcherWrapper(appSwitcher)
    : appSwitcher

  return (
    <NavbarAnnouncements>
      <Navbar environment={environment}>
        <NavbarSection gap="md" share="fill">
          <Stack align="center" direction="horizontal" gap="xs">
            <NavbarBrand asChild>
              <Link to={homePath}>{`Joyful Health`}</Link>
            </NavbarBrand>
            {renderedAppSwitcher}
            <DemoModeIndicator />
          </Stack>
          <OverflowNavbarLinkList
            items={navbarLinkItems}
            renderVisibleItem={renderPrimaryItem}
            visibility="desktop"
          />
        </NavbarSection>

        <NavbarSection gap="sm" share="fixed" visibility="desktop">
          {rightStartAdornment}
          <AskAiNavbarButton />
          <AgentActivityNavbarButton />
          <NavbarAccountSwitcher
            isPending={isAccountSwitchPending}
            manageTeamHref={manageTeamHref}
            onOpenFromHotkey={() => {
              window.scrollTo({
                behavior: 'instant',
                top: 0,
              })
            }}
            onSelectAccount={onSelectAccount}
          />
          <NavbarUserMenu
            accountItems={accountItems}
            currentUserSubmenuItems={currentUserSubmenuItems}
            frontendCommitMessage={frontendCommitMessage}
            frontendCommitSha={frontendCommitSha}
            frontendCommitTimestamp={frontendCommitTimestamp}
            generalItems={generalItems}
            helpMenuItem={helpMenuItem ?? <NavbarAnnouncementsHelpMenuItem />}
            renderTrigger={(trigger) => {
              const hintedTrigger = (
                <AnnouncementsMenuHintPopover>
                  {trigger}
                </AnnouncementsMenuHintPopover>
              )
              if (renderUserMenuTrigger) {
                return renderUserMenuTrigger(hintedTrigger)
              }

              return hintedTrigger
            }}
            setDemoMode={setDemoMode}
            utilityMenuItems={utilityMenuItems}
          />
        </NavbarSection>

        <JoyfulWebMobileNavigationSheet
          accountItems={accountItems}
          frontendCommitMessage={frontendCommitMessage}
          frontendCommitSha={frontendCommitSha}
          frontendCommitTimestamp={frontendCommitTimestamp}
          generalItems={generalItems}
          homePath={homePath}
          isAccountSwitchPending={isAccountSwitchPending}
          accountHref={accountHref}
          manageTeamHref={manageTeamHref}
          primaryItems={primaryItems}
          onSelectAccount={onSelectAccount}
        />
      </Navbar>
    </NavbarAnnouncements>
  )
}

function createOverflowNavbarLinkListItems({
  items,
  pathname,
}: {
  items: ReadonlyArray<AppNavigationItem>
  pathname: string
}): Array<OverflowNavbarLinkListItem> {
  const navbarLinkItems: Array<OverflowNavbarLinkListItem> = []

  for (const item of items) {
    if (item.type === 'group') {
      navbarLinkItems.push({
        children: item.title,
        isActive: item.items.some((child) => {
          return child.path !== '/' && pathname.startsWith(child.path)
        }),
        items: item.items.map((child) => {
          return {
            children: child.title,
            endAdornment: child.endAdornment,
            isActive: child.path !== '/' && pathname.startsWith(child.path),
            key: child.key,
            to: getAppNavigationLinkTo(child),
          }
        }),
        key: item.key,
      })
    } else if (item.path) {
      navbarLinkItems.push({
        children: item.title,
        endAdornment: item.endAdornment,
        isActive: item.path !== '/' && pathname.startsWith(item.path),
        key: item.key,
        to: getAppNavigationLinkTo(item),
      })
    }
  }

  return navbarLinkItems
}

export interface NavbarAccountSwitcherProps extends Partial<ButtonProps> {
  isPending?: boolean
  manageTeamHref?: string | null
  onNavigate?: () => void
  onOpenFromHotkey?: () => void
  onSelectAccount: (account: CurrentUserAccountRead) => void
  popoverProps?: Partial<
    Omit<PopoverProps, 'children' | 'open' | 'onOpenChange'>
  >
}

export function NavbarAccountSwitcher({
  isPending = false,
  manageTeamHref = null,
  onNavigate,
  onOpenFromHotkey,
  onSelectAccount,
  popoverProps,
  ...buttonProps
}: NavbarAccountSwitcherProps) {
  const [isPopoverOpen, setPopoverOpen] = useState(false)
  const currentUser = useCurrentUser()
  const activeAccount = useActiveAccount()
  const hasDebugCapability = useHasUserCapability('admin.debug')
  const canViewMembers = useHasUserCapability('account.members.view')
  const canSelectFromAccounts =
    currentUser.accounts.length > 1 || activeAccount === null

  const visibleMembershipsQuery = useApiQuery(
    'get',
    '/accounts/{org_slug}/memberships',
    {
      params: {
        path: {
          org_slug: activeAccount?.slug ?? '',
        },
        query: {
          limit: 1,
          page: 1,
        },
      },
    },
    {
      enabled: isPopoverOpen && canViewMembers && activeAccount !== null,
      staleTime: 5 * 60 * 1000,
    },
  )

  const visibleMemberCountLabel =
    visibleMembershipsQuery.isSuccess === true
      ? pluralize(visibleMembershipsQuery.data.total_count, 'member')
      : null

  useHotkey(
    GlobalHotkeys.accountSelection,
    () => {
      setPopoverOpen(true)
      onOpenFromHotkey?.()
    },
    { preventDefault: true },
  )

  if (currentUser.accounts.length === 0) {
    return null
  }

  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={setPopoverOpen}
      {...popoverProps}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          isPending={isPending}
          endIcon={<ChevronsUpDownIcon className="size-3.5!" />}
          colorVariant={activeAccount === null ? 'muted' : 'default'}
          weight={activeAccount === null ? 'normal' : 'medium'}
          tooltip={{
            content:
              activeAccount === null
                ? 'Select account'
                : currentUser.accounts.length > 1
                  ? 'Change account'
                  : 'View account',
            hotkey: GlobalHotkeys.accountSelection,
            tooltipRootProps: {
              delayDuration: 750,
            },
          }}
          {...buttonProps}
        >
          <span className="truncate">
            {activeAccount?.name ? (
              <Redact>{activeAccount.name}</Redact>
            ) : (
              'Select account…'
            )}
          </span>
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" size="sm" padding="none">
        <AccountSelectionCommand
          state={{
            accounts: currentUser.accounts,
            activeAccount,
            isDisabled: false,
            size: 'md',
          }}
          actions={{
            onSelect: (account) => {
              if (account !== null) {
                const selectedAccount =
                  currentUser.accounts.find((currentUserAccount) => {
                    return currentUserAccount.id === account.id
                  }) ?? null

                if (selectedAccount !== null) {
                  setActiveOrgSlugCookie(selectedAccount.slug)
                  onSelectAccount(selectedAccount)
                  onNavigate?.()
                }
              }

              setPopoverOpen(false)
            },
          }}
        >
          <AccountSelectionCommandHeader
            subtitle={
              activeAccount ? (
                <Enumerate separator=" · ">
                  {[
                    hasDebugCapability ? (
                      <Copyable key="slug">{activeAccount.slug}</Copyable>
                    ) : null,
                    visibleMemberCountLabel,
                  ].filter(Boolean)}
                </Enumerate>
              ) : null
            }
            endAdornment={
              manageTeamHref ? (
                <RestrictToCapability capability="account.members.view">
                  <Button
                    size="sm"
                    variant="outline"
                    asChild
                    onClick={() => {
                      onNavigate?.()
                      setPopoverOpen(false)
                    }}
                  >
                    <a href={manageTeamHref}>
                      <RestrictToCapability
                        capability={UserRoleManagementCapabilities}
                        fallback={`Team`}
                      >
                        {`Manage team`}
                      </RestrictToCapability>
                    </a>
                  </Button>
                </RestrictToCapability>
              ) : null
            }
          />

          {canSelectFromAccounts ? (
            <AccountSelectionCommandInput placeholder="Switch to account…" />
          ) : null}
          {canSelectFromAccounts ? (
            <AccountSelectionCommandList maxHeight="2xl">
              <AccountSelectionCommandGroupedOptions />
            </AccountSelectionCommandList>
          ) : null}
        </AccountSelectionCommand>
      </PopoverContent>
    </Popover>
  )
}

interface JoyfulWebMobileNavigationSheetProps {
  accountHref: string
  accountItems: ReadonlyArray<AppNavigationItem>
  frontendCommitMessage?: string | null
  frontendCommitSha?: string | null
  frontendCommitTimestamp?: string | null
  generalItems: ReadonlyArray<AppNavigationItem>
  homePath: string
  isAccountSwitchPending: boolean
  manageTeamHref: string | null
  onSelectAccount: (account: CurrentUserAccountRead) => void
  primaryItems: ReadonlyArray<AppNavigationItem>
}

function JoyfulWebMobileNavigationSheet({
  accountHref,
  accountItems,
  frontendCommitMessage = null,
  frontendCommitSha = null,
  frontendCommitTimestamp = null,
  generalItems,
  homePath,
  isAccountSwitchPending,
  manageTeamHref,
  onSelectAccount,
  primaryItems,
}: JoyfulWebMobileNavigationSheetProps) {
  const currentUser = useCurrentUser()
  const activeAccount = useActiveAccount()
  const globalCommand = useGlobalCommand()
  const canImpersonateRoles = UserRoleSchema.meta(
    currentUser.role,
  ).canImpersonateRoles
  const leaveFeedbackContext = useLeaveFeedbackContext()
  const proposeChangeContext = useProposeChangeContext()
  const [isOpen, setOpen] = useState(false)
  const secondaryAccountItems = accountItems.filter((item) => {
    const isPrimaryItem = primaryItems.some((primaryItem) => {
      return primaryItem.key === item.key
    })

    return !isPrimaryItem
  })
  const activeAccountItems = [...primaryItems, ...secondaryAccountItems]

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <div className="self-center lg:@3xl/navbar:hidden">
          <IconButton
            size="sm"
            colorVariant="muted"
            variant="ghost"
            aria-label={isOpen === true ? 'Close menu' : 'Open menu'}
          >
            {isOpen === true ? <XIcon /> : <MenuIcon />}
          </IconButton>
        </div>
      </SheetTrigger>

      <SheetContent
        layout="column"
        onOpenAutoFocus={(event) => {
          return event.preventDefault()
        }}
        padding="none"
      >
        <SheetHeader layout="toolbar" padding="sm">
          <SheetTitle>
            {currentUser.accounts.length > 0 ? (
              <NavbarAccountSwitcher
                isPending={isAccountSwitchPending}
                manageTeamHref={manageTeamHref}
                onNavigate={() => {
                  setOpen(false)
                }}
                onSelectAccount={onSelectAccount}
                variant="outline"
                popoverProps={{ modal: true }}
              />
            ) : (
              <NavbarBrand asChild size="md">
                <Link to={homePath}>{`Joyful Health`}</Link>
              </NavbarBrand>
            )}
          </SheetTitle>

          <Stack align="center" direction="horizontal" gap="xs">
            <IconButton
              size="sm"
              colorVariant="muted"
              variant="ghost"
              aria-label={`Open command menu`}
              onClick={() => {
                setOpen(false)
                globalCommand.open()
              }}
            >
              <SearchIcon />
            </IconButton>
            <SheetCloseIconButton size="sm" />
          </Stack>
        </SheetHeader>

        <Separator />

        <ItemGroup>
          <CurrentUserItem
            accountHref={accountHref}
            size="lg"
            onClick={() => {
              setOpen(false)
            }}
          />
        </ItemGroup>

        {canImpersonateRoles ? (
          <Box paddingX="md" paddingY="sm">
            <Stack align="start">
              <MobileRoleImpersonationMenu />
            </Stack>
          </Box>
        ) : null}

        <Separator thickness="thick" />

        <Stack className="scrollable-y py-2" overflow="contain">
          {activeAccountItems.length > 0 ? (
            <>
              <ItemGroup>
                {activeAccount?.name ? (
                  <ItemGroupLabel>
                    <Redact>{activeAccount.name}</Redact>
                  </ItemGroupLabel>
                ) : null}

                {activeAccountItems.map((item) => {
                  return (
                    <MobileMenuNavigationItem
                      key={item.key}
                      item={item}
                      onClick={() => {
                        setOpen(false)
                      }}
                    />
                  )
                })}
              </ItemGroup>
              <ItemSeparator />
            </>
          ) : null}

          {generalItems.length > 0 ? (
            <>
              <ItemGroup>
                {generalItems.map((item) => {
                  return (
                    <MobileMenuNavigationItem
                      key={item.key}
                      item={item}
                      onClick={() => {
                        setOpen(false)
                      }}
                    />
                  )
                })}
              </ItemGroup>
              <ItemSeparator />
            </>
          ) : null}

          <ItemGroup>
            <ItemGroupLabel>{`Help`}</ItemGroupLabel>
            <LeaveFeedbackItem
              size="lg"
              onLeaveFeedback={() => {
                setOpen(false)
                leaveFeedbackContext.actions.openFeedbackDialog()
              }}
            />
            <RestrictToCapability capability="product_change.propose">
              <ProposeChangeItem
                size="lg"
                onProposeChange={() => {
                  setOpen(false)
                  proposeChangeContext.actions.openProposeChangeDialog()
                }}
              />
            </RestrictToCapability>
            <SignOutItem size="lg" />
          </ItemGroup>

          <RestrictToCapability capability="deployment.view">
            <ItemSeparator />
            <ItemGroup>
              <DeploymentInfo
                frontendCommitMessage={frontendCommitMessage}
                frontendCommitSha={frontendCommitSha}
                frontendCommitTimestamp={frontendCommitTimestamp}
              />
            </ItemGroup>
          </RestrictToCapability>
        </Stack>
      </SheetContent>
    </Sheet>
  )
}

interface MobileMenuNavigationItemProps extends Omit<ItemProps, 'asChild'> {
  item: AppNavigationItem
}

function MobileMenuNavigationItem({
  item,
  ...props
}: MobileMenuNavigationItemProps) {
  const { pathname } = useLocation()

  if (item.type === 'group') {
    return (
      <>
        <ItemGroupLabel>{item.title}</ItemGroupLabel>
        {item.items.map((child) => {
          return (
            <MobileMenuNavigationItem key={child.key} item={child} {...props} />
          )
        })}
      </>
    )
  }

  if (!item.path) {
    const onSelect = item.onSelect
    if (!onSelect) {
      return null
    }

    return (
      <Item
        isInteractive
        size="lg"
        {...props}
        onClick={(event) => {
          props.onClick?.(event)
          onSelect()
        }}
      >
        <ItemContent>{item.title}</ItemContent>
      </Item>
    )
  }

  const isActive = pathname.startsWith(item.path)
  return (
    <Item
      key={item.key}
      asChild
      isInteractive
      isSelected={isActive}
      size="lg"
      aria-current={isActive ? 'page' : undefined}
      {...props}
    >
      <Link to={getAppNavigationLinkTo(item)}>
        <ItemContent>{item.title}</ItemContent>
      </Link>
    </Item>
  )
}

interface CurrentUserItemProps extends Omit<ItemProps, 'asChild'> {
  accountHref: string
}

function CurrentUserItem({ accountHref, ...props }: CurrentUserItemProps) {
  const currentUser = useCurrentUser()
  const roleMeta = UserRoleSchema.meta(currentUser.effective_role)
  const isImpersonatingRole = currentUser.impersonated_role ? true : false

  return (
    <Item asChild isInteractive {...props}>
      <a href={accountHref}>
        <span className="mt-1 self-start">
          <CurrentUserAvatar size="md" />
        </span>
        <ItemContent>
          <p className="font-medium">{currentUser.name ?? currentUser.email}</p>
          {currentUser.name && currentUser.email ? (
            <ItemSubtitle>{currentUser.email}</ItemSubtitle>
          ) : null}
          <Tooltip title={roleMeta.title} content={roleMeta.description}>
            {isImpersonatingRole ? (
              <ItemSubtitle asChild tone="inherit" weight="medium">
                <span className="text-tremor-brand-emphasis">
                  {`Impersonating ${roleMeta.title}`}
                </span>
              </ItemSubtitle>
            ) : (
              <ItemSubtitle>{roleMeta.title}</ItemSubtitle>
            )}
          </Tooltip>
        </ItemContent>
      </a>
    </Item>
  )
}

interface CurrentUserAvatarProps {
  size?: 'sm' | 'md' | 'lg'
}

function CurrentUserAvatar({ size = 'md' }: CurrentUserAvatarProps) {
  const currentUser = useCurrentUser()
  const isImpersonatingRole = currentUser.impersonated_role ? true : false

  return (
    <span className="relative inline-flex">
      <UserAvatar
        userId={currentUser.id}
        imageUrl={currentUser.image_url ?? null}
        isCurrentUser
        name={currentUser.name ?? null}
        ring={isImpersonatingRole ? 'impersonating' : undefined}
        size={size}
      />
      {isImpersonatingRole ? (
        <span
          className="bg-tremor-brand border-tremor-background absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full border-2"
          aria-hidden
        />
      ) : null}
    </span>
  )
}

function SignOutItem({ ...props }: Omit<ItemProps, 'asChild'>) {
  const auth = useAuth()

  return (
    <Item
      isInteractive
      {...props}
      onClick={(event) => {
        props.onClick?.(event)
        void auth.signOut()
      }}
    >
      {`Sign out`}
    </Item>
  )
}
